import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { API_BASE_URL, useApi } from './client.js';

export interface AuditEntry {
  id: string;
  organisation_id: string | null;
  actor_id: string | null;
  actor_display_name: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  payload: Record<string, unknown>;
  occurred_at: string;
}

export interface AuditListParams {
  action?: string[];
  actor_id?: string[];
  target_type?: string;
  target_id?: string;
  from?: string;
  to?: string;
  limit?: number;
}

function buildQuery(params: AuditListParams): string {
  const qs = new URLSearchParams();
  if (params.action?.length) qs.set('action', params.action.join(','));
  if (params.actor_id?.length) qs.set('actor_id', params.actor_id.join(','));
  if (params.target_type) qs.set('target_type', params.target_type);
  if (params.target_id) qs.set('target_id', params.target_id);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export function useAuditList(params: AuditListParams) {
  const api = useApi();
  return useQuery({
    queryKey: ['audit', 'list', params],
    queryFn: () =>
      api<{ entries: AuditEntry[]; next_cursor: string | null }>(`/audit-log${buildQuery(params)}`),
    staleTime: 10_000,
  });
}

export function useAuditActions() {
  const api = useApi();
  return useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: () => api<{ actions: string[] }>('/audit-log/actions'),
    staleTime: 5 * 60_000,
  });
}

// Triggers a CSV download with the current filters. Uses a raw fetch so we can attach
// the Clerk bearer token + stream the blob to the browser.
export function useAuditCsv() {
  const { getToken } = useAuth();
  return useCallback(
    async (params: AuditListParams): Promise<void> => {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/audit-log.csv${buildQuery(params)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`CSV export failed: ${res.status} ${text.slice(0, 120)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    [getToken],
  );
}
